import { useEffect, useRef } from 'react';
import { useLocation } from 'wouter';
import { useTranslation } from 'react-i18next';
import { toast } from '@/hooks/use-toast';
import { READ_ONLY_DEFAULTS, useReadOnlyMessage } from '@/hooks/use-read-only-message';
import { isNative } from '@/platform/native';
import { useAuth } from '@/contexts/auth-context';
import { ToastAction } from '@/components/ui/toast';
import { READ_ONLY_REFUSED_EVENT, type ReadOnlyRefusal } from '@/lib/billing-events';

/** Autosave can be refused on every keystroke pause; one toast per burst is enough. */
const REPEAT_AFTER_MS = 5000;

/**
 * Every write the server refuses with 403 `subscription_required` ends up here (api-client fires the event), so a
 * read-only account is always told why, whatever the screen does with the error. Screens may also show the same
 * sentence inline next to the action (useReadOnlyMessage).
 */
export function ReadOnlyRefusalToast() {
  // Only a signed-in user's writes can be refused; signed out, nothing is mounted and no billing copy is fetched.
  const { user } = useAuth();
  return user ? <RefusalListener /> : null;
}

function RefusalListener() {
  const { t } = useTranslation('subscription', { useSuspense: false });
  const { forRefusal, isAdmin } = useReadOnlyMessage();
  const [, setLocation] = useLocation();
  const last = useRef(0);

  useEffect(() => {
    const onRefused = (event: Event) => {
      const now = Date.now();
      if (now - last.current < REPEAT_AFTER_MS) return;
      last.current = now;
      const refusal = (event as CustomEvent<ReadOnlyRefusal | null>).detail;
      // The event fires inside the HTTP client, before the screen's own catch runs. Only one toast shows at a time,
      // so wait one task: a screen's generic "That didn't save" toast can then never replace the real reason.
      setTimeout(() => toast({
        title: t('billing.refused.title', READ_ONLY_DEFAULTS.title),
        description: forRefusal(refusal),
        variant: 'destructive',
        action: isAdmin && !isNative() && refusal?.reason !== 'account_on_hold' ? (
          <ToastAction altText={t('billing.choosePlan', READ_ONLY_DEFAULTS.choosePlan)} onClick={() => setLocation('/admin/billing')}>
            {t('billing.choosePlan', READ_ONLY_DEFAULTS.choosePlan)}
          </ToastAction>
        ) : undefined,
      }), 0);
    };
    window.addEventListener(READ_ONLY_REFUSED_EVENT, onRefused);
    return () => window.removeEventListener(READ_ONLY_REFUSED_EVENT, onRefused);
  }, [forRefusal, isAdmin, setLocation, t]);

  return null;
}
